/** Auswertungen (E9.4): Zeitraum, KPIs mit Vorjahr, Verlauf, Regale, Kategorien, Top-Mieter, Zahlungsarten, Leerstand, CSV. Nur Tokens, Balken aus CSS. */
import { useState } from "react";
import { Link } from "react-router";
import { Seitenzustand, TextField } from "../ui";
import { Card, KpiTile, PageHeader } from "../ui/Layout";
import { formatEuro } from "@shared/geld";
import { formatTag, tagVon } from "@shared/zeit";
import { tagPlus } from "@shared/perioden";
import { useAuswertung, type Auswertung } from "./geld-v2-api";
import { useQuery } from "@tanstack/react-query";
import { api, rufe } from "../api/client";

function delta(a: number, b: number): string {
  if (!b) return a ? "neu" : "–";
  const p = Math.round(((a - b) / b) * 100);
  return `${p > 0 ? "+" : ""}${p} % zum Vorjahr`;
}

export function Auswertungen() {
  const heute = tagVon();
  const [von, setVon] = useState(`${heute.slice(0, 7)}-01`);
  const [bis, setBis] = useState(heute);
  const q = useAuswertung(von, bis);
  const schnell = (tage: number) => { setVon(tagPlus(heute, -tage + 1)); setBis(heute); };
  return (
    <>
      <PageHeader kicker="Geld" title="Auswertungen" sub="Umsatz, Provision, Miete, Auslastung – je Zeitraum, mit Vorjahr" actions={<a href={`/api/laden/auswertungen.csv?von=${von}&bis=${bis}`} className="rg-btn rg-btn--secondary">CSV</a>} />
      <div className="sc-toolbar" style={{ marginBottom: "var(--space-4)" }}>
        <button type="button" className="sc-pill" onClick={() => schnell(7)}>7 Tage</button>
        <button type="button" className="sc-pill" onClick={() => schnell(30)}>30 Tage</button>
        <button type="button" className="sc-pill" onClick={() => { setVon(`${heute.slice(0, 7)}-01`); setBis(heute); }}>Dieser Monat</button>
        <button type="button" className="sc-pill" onClick={() => { setVon(`${heute.slice(0, 4)}-01-01`); setBis(heute); }}>Dieses Jahr</button>
        <span className="sc-grow" />
        <TextField label="Von" type="date" value={von} onChange={(e) => setVon(e.target.value)} />
        <TextField label="Bis" type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
      </div>
      <Seitenzustand query={q} lines={8}>{(d) => <Inhalt d={d} />}</Seitenzustand>
      <GutscheineKarte />
    </>
  );
}

/** E11.3: Ausgegebene Gutscheine – offener Wert ist eine Verbindlichkeit des Ladens. */
function GutscheineKarte() {
  const q = useQuery({ queryKey: ["laden", "gutscheine"], queryFn: () => rufe<{ gutscheine: { id: string; code: string; wertCent: number; restCent: number; status: string; gueltigBis: string | null; erstelltAt: string }[]; offenCent: number; anzahlAktiv: number }>(api.api.laden.gutscheine.$get()) });
  const d = q.data;
  if (!d || !d.gutscheine.length) return null;
  const STATUS: Record<string, string> = { offen: "Noch nicht bezahlt", aktiv: "Aktiv", aufgebraucht: "Aufgebraucht", storniert: "Storniert" };
  return (
    <Card title={`Gutscheine · ${d.anzahlAktiv} aktiv · ${formatEuro(d.offenCent)} offen`} flush>
      <div className="app-tabellen-scroll">
        <table className="rg-table">
          <thead><tr><th>Code</th><th className="is-num">Wert</th><th className="is-num">Rest</th><th>Status</th><th>Gültig bis</th><th>Verkauft</th></tr></thead>
          <tbody>{d.gutscheine.slice(0, 50).map((g) => <tr key={g.id}><td className="app-code">{g.code}</td><td className="is-num">{formatEuro(g.wertCent)}</td><td className="is-num">{formatEuro(g.restCent)}</td><td>{STATUS[g.status] ?? g.status}</td><td>{g.gueltigBis ? formatTag(g.gueltigBis) : "–"}</td><td>{formatTag(g.erstelltAt.slice(0, 10))}</td></tr>)}</tbody>
        </table>
      </div>
      <p className="sc-muted sc-small" style={{ padding: "var(--space-3) var(--space-4)" }}>Verkauf und Einlösung laufen über die Kasse (Knopf „Gutschein“). Der offene Wert gehört nicht zum Umsatz, bis er eingelöst ist.</p>
    </Card>
  );
}

function Balken({ zeilen, max }: { zeilen: { key: string; anzahl: number; umsatzCent: number; to?: string }[]; max: number }) {
  return (
    <div className="sc-col" style={{ gap: 6 }}>
      {zeilen.map((z) => (
        <div key={z.key} className="sc-row" style={{ gap: "var(--space-2)" }}>
          <span className="sc-small" style={{ width: 140, flex: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{z.to ? <Link to={z.to}>{z.key}</Link> : z.key}</span>
          <div className="sc-progress" aria-hidden="true"><span style={{ width: `${max ? Math.round((z.umsatzCent / max) * 100) : 0}%` }} /></div>
          <span className="sc-small sc-muted" style={{ width: 120, textAlign: "right" }}>{formatEuro(z.umsatzCent)} · {z.anzahl}</span>
        </div>
      ))}
    </div>
  );
}

function Inhalt({ d }: { d: Auswertung }) {
  const k = d.kpis;
  const v = d.vorjahr;
  const maxVerlauf = Math.max(1, ...d.verlauf.map((x) => x.umsatzCent));
  return (
    <div className="sc-col" style={{ gap: "var(--space-4)" }}>
      <div className="app-kpis">
        <KpiTile label="Umsatz Mieterware" value={formatEuro(k.umsatzCent)} note={`${k.verkauft} Artikel · ${delta(k.umsatzCent, v.umsatzCent)}`} />
        <KpiTile label="Erlös des Ladens" value={formatEuro(k.ladenErloesCent)} note={`Provision ${formatEuro(k.provisionCent)} + Miete ${formatEuro(k.mieteCent)} · ${delta(k.ladenErloesCent, v.ladenErloesCent)}`} />
        <KpiTile label="Auslastung" value={`${k.auslastungProzent} %`} note={`${k.regale} Regale · ${k.leerstandTage} leere Regaltage`} />
        <KpiTile label="Rückgaben" value={formatEuro(k.rueckgabenCent)} note={`${k.rueckgaben} Stück · Rabatte ${formatEuro(k.rabatteCent)}`} />
        <KpiTile label="Ausgezahlt" value={formatEuro(k.auszahlungenCent)} note={delta(k.auszahlungenCent, v.auszahlungenCent)} />
      </div>
      <Card title={`Verlauf ${formatTag(d.von)} – ${formatTag(d.bis)}`}>
        <div className="app-verlauf" role="img" aria-label={`Umsatz je ${d.verlauf.length > 60 ? "Woche" : "Tag"}`}>
          {d.verlauf.map((x) => <span key={x.tag} className={x.umsatzCent > 0 ? "is-on" : undefined} style={{ height: `${Math.max(4, (x.umsatzCent / maxVerlauf) * 100)}%` }} title={`${formatTag(x.tag)}: ${formatEuro(x.umsatzCent)} (${x.anzahl})`} />)}
        </div>
        <div className="sc-spread sc-small sc-muted"><span>{formatTag(d.von)}</span><span>{d.verlauf.length > 60 ? "je Woche" : "je Tag"}</span><span>{formatTag(d.bis)}</span></div>
      </Card>
      <div className="app-zweispaltig">
        <Card title="Regale">{d.regale.length ? <Balken zeilen={d.regale.map((r) => ({ ...r, key: `Regal ${r.key}` }))} max={d.regale[0]?.umsatzCent ?? 0} /> : <p className="sc-muted">Keine Verkäufe im Zeitraum.</p>}</Card>
        <Card title="Kategorien">{d.kategorien.length ? <Balken zeilen={d.kategorien} max={d.kategorien[0]?.umsatzCent ?? 0} /> : <p className="sc-muted">Keine Verkäufe im Zeitraum.</p>}</Card>
        <Card title="Top-Mieter">{d.mieter.length ? <Balken zeilen={d.mieter} max={d.mieter[0]?.umsatzCent ?? 0} /> : <p className="sc-muted">Keine Verkäufe im Zeitraum.</p>}</Card>
        <Card title="Zahlungsarten">
          {d.zahlungsarten.length ? <Balken zeilen={d.zahlungsarten.map((z) => ({ key: z.zahlungsart === "bar" ? "Bar" : z.zahlungsart === "karte" ? "Karte" : "Sonstige", anzahl: z.anzahl, umsatzCent: z.summeCent }))} max={Math.max(...d.zahlungsarten.map((z) => z.summeCent))} /> : <p className="sc-muted">Keine bezahlten Warenkörbe im Zeitraum.</p>}
          <p className="sc-muted sc-small" style={{ marginTop: "var(--space-3)" }}>Zahlungsarten kommen aus SumUp. Beträge je Warenkorb inklusive Ladenware.</p>
        </Card>
      </div>
    </div>
  );
}
